import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Divider,
    Chip,
    Tabs,
    Tab
} from '@mui/material';
import {
    Close as CloseIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Height as HeightIcon,
    FitnessCenter as WeightIcon,
    MonitorWeight as BMIIcon,
    LocationOn as LocationIcon,
    MedicalServices as SymptomsIcon,
    Assessment as AssessmentIcon,
    Info as InfoIcon,
    DateRange as DateIcon,
    DirectionsRun as ExerciseIcon,
    Psychology as GoalIcon,
    Notifications as NotificationIcon,
    Watch as DeviceIcon,
    Warning as WarningIcon,
    Notes as NotesIcon
} from '@mui/icons-material';
import api from '../../api/api';
import en_ja from '../../en-ja-map';

const PatientDetailsDialog = ({ open, onClose, patientData, appointmentData }) => {
    const [userQuery, setUserQuery] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [finalConsultationAnswers, setFinalConsultationAnswers] = useState([]);

    const getQuestionsOne = () => {
        const controller = new AbortController();

        api.get(`v1/auth/user/${patientData.id}`, { signal: controller.signal })
            .then((res) => {
                setUserQuery(res.data.data.answers || []);
            })
            .catch((err) => {
                if (err.name !== 'CanceledError') {
                    console.error('Failed to fetch patient data:', err);
                }
            });

        return () => controller.abort();
    }

    const getQuestionsTwo = () => {
        api.get(`v1/user/get/final-consulation/answers/${patientData.id}`)
            .then((res) => {
                console.log('Final consultation answers:', res.data.data.consultation_answers);
                setFinalConsultationAnswers(res.data.data.consultation_answers || []);
            })
            .catch((err) => {
                if (err.name !== 'CanceledError') {
                    console.error('Failed to fetch patient data:', err);
                }
            });
    }

    useEffect(() => {
        console.log('PatientDetailsDialog props changed:', { patientData, appointmentData });
        if (!open || !patientData?.id) return;

        getQuestionsOne();
        getQuestionsTwo();

    }, [open, patientData?.id]);

    if (!patientData || !appointmentData) return null;

    const rawCheckin = appointmentData.clinic_checkin_data;
    const checkinData = typeof rawCheckin === 'string'
        ? (() => { try { return JSON.parse(rawCheckin) } catch { return {} } })()
        : (rawCheckin || {});

    const heightCm = Number(checkinData?.height) || null;
    const weightKg = Number(checkinData?.weight) || null;
    const providedBMI = Number(checkinData?.bmi);
    const bmiValue = Number.isFinite(providedBMI)
        ? providedBMI
        : (heightCm && weightKg
            ? (weightKg / Math.pow(heightCm / 100, 2))
            : null);

    const normalizeBMI = (bmi) => {
        if (!Number.isFinite(bmi)) return null;
        return bmi > 1000 ? +(bmi / 1000).toFixed(1) : +bmi.toFixed(1);
    };

    const safeBMI = normalizeBMI(bmiValue);

    const getBMICategory = (bmi) => {
        if (!Number.isFinite(bmi)) return null;
        const n = bmi > 1000 ? bmi / 1000 : bmi;
        if (n < 18.5) return { label: (en_ja['Underweight'] || 'Underweight'), color: '#2196F3' };
        if (n < 25) return { label: (en_ja['Normal'] || 'Normal'), color: '#4CAF50' };
        if (n < 30) return { label: (en_ja['Overweight'] || 'Overweight'), color: '#FF9800' };
        return { label: (en_ja['Obese'] || 'Obese'), color: '#f44336' };
    };

    const bmiCategory = getBMICategory(bmiValue);

    const hasAnyMedical =
        Boolean(heightCm) || Boolean(weightKg) || Number.isFinite(safeBMI) ||
        Boolean(checkinData?.sex) || Boolean(checkinData?.address) || Boolean(checkinData?.symptoms);

    const hasNotes = Boolean(appointmentData?.notes?.trim());

    const getQuestionIcon = (questionText) => {
        const text = questionText.toLowerCase();
        if (text.includes('age') || text.includes('dob')) return <DateIcon sx={{ color: '#dc3545', fontSize: 20 }} />;
        if (text.includes('sex') || text.includes('gender')) return <PersonIcon sx={{ color: '#fd7e14', fontSize: 20 }} />;
        if (text.includes('height') || text.includes('weight')) return <HeightIcon sx={{ color: '#007bff', fontSize: 20 }} />;
        if (text.includes('exercise') || text.includes('activity')) return <ExerciseIcon sx={{ color: '#28a745', fontSize: 20 }} />;
        if (text.includes('goal') || text.includes('target')) return <GoalIcon sx={{ color: '#6f42c1', fontSize: 20 }} />;
        if (text.includes('device')) return <DeviceIcon sx={{ color: '#17a2b8', fontSize: 20 }} />;
        if (text.includes('notification')) return <NotificationIcon sx={{ color: '#ffc107', fontSize: 20 }} />;
        if (text.includes('medical') || text.includes('blood pressure')) return <SymptomsIcon sx={{ color: '#dc3545', fontSize: 20 }} />;
        return <AssessmentIcon sx={{ color: '#6c757d', fontSize: 20 }} />;
    };

    const getHealthWarnings = () => {
        const warnings = [];
        const targetWeight = userQuery.find(q => q.question_text.toLowerCase().includes('target weight'));
        const heightAnswer = userQuery.find(q => q.question_text.toLowerCase().includes('height'));
        const age = userQuery.find(q => q.question_text.toLowerCase().includes('age'));
        const conditions = userQuery.find(q => q.question_text.toLowerCase().includes('following'));

        if (targetWeight && heightAnswer) {
            const weight = parseInt(targetWeight.answer_text);
            const height = heightAnswer.types?.find(t => t.type === 'Height')?.answer_text;
            if (weight && height && weight < 40) {
                warnings.push({
                    type: 'critical',
                    message: (en_ja['Target weight appears dangerously low. Medical review recommended.'] || 'Target weight appears dangerously low. Medical review recommended.')
                });
            }
        }

        if (age && conditions && parseInt(age.answer_text) < 25) {
            const hasHighBP = conditions.options?.some(opt =>
                opt.option_text.toLowerCase().includes('high blood pressure')
            );
            if (hasHighBP) {
                warnings.push({
                    type: 'warning',
                    message: (en_ja['High blood pressure reported in young patient.'] || 'High blood pressure reported in young patient.')
                });
            }
        }

        return warnings;
    };

    const healthWarnings = getHealthWarnings();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                }
            }}
        >
            <DialogTitle sx={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2.5, px: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1.1rem', letterSpacing: '-0.02em' }}>
                    {en_ja['Patient Details'] || 'Patient Details'}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{
                    backgroundColor: 'white', width: 32, height: 32, border: '1px solid #e9ecef',
                    '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#dc3545', '& .MuiSvgIcon-root': { color: '#dc3545' } }
                }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <Box sx={{ p: 3, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: '#dc3545', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                        <PersonIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '1rem', mb: 0.5 }}>
                            {checkinData?.full_name || patientData?.name || '—'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem' }}>
                            {(en_ja['Patient ID:'] || 'Patient ID:')} {patientData?.id ?? '—'}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                        <EmailIcon sx={{ color: '#dc3545', mr: 1, fontSize: 16 }} />
                        <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                {en_ja['Email'] || 'Email'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.8rem' }}>
                                {patientData?.email || '—'}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                        <PhoneIcon sx={{ color: '#dc3545', mr: 1, fontSize: 16 }} />
                        <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                {en_ja['Phone'] || 'Phone'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.8rem' }}>
                                {patientData?.phone_number || '—'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ borderBottom: '1px solid #e9ecef' }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: '#6c757d',
                            '&.Mui-selected': {
                                color: '#dc3545'
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#dc3545'
                        }
                    }}
                >
                    <Tab
                        icon={<InfoIcon fontSize="small" />}
                        label={en_ja['Details'] || 'Details'}
                        iconPosition="start"
                        sx={{ px: 3 }}
                    />
                    <Tab
                        icon={<AssessmentIcon fontSize="small" />}
                        label={en_ja['Health Assessment'] || 'Health Assessment'}
                        iconPosition="start"
                        sx={{ px: 3 }}
                    />
                    {hasNotes && (
                        <Tab
                            icon={<NotesIcon fontSize="small" />}
                            label={en_ja['Notes'] || 'Notes'}
                            iconPosition="start"
                            sx={{ px: 3 }}
                        />
                    )}
                </Tabs>
            </Box>

            <DialogContent sx={{ p: 0 }}>

                {activeTab === 0 && (
                    <Box sx={{ p: 3 }}>

                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '1rem', mb: 2 }}>
                            {en_ja['Medical Information'] || 'Medical Information'}
                        </Typography>

                        {!hasAnyMedical && (
                            <Box sx={{ p: 2, borderRadius: '12px', border: '1px dashed #e9ecef', color: '#666', fontSize: '0.875rem', mb: 3 }}>
                                {en_ja['No medical information available.'] || 'No medical information available.'}
                            </Box>
                        )}

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2, mb: 3 }}>
                            {heightCm && (
                                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                                    <HeightIcon sx={{ color: '#007bff', mr: 1.5, fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            {en_ja['Height'] || 'Height'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.875rem' }}>
                                            {heightCm} cm
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {weightKg && (
                                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                                    <WeightIcon sx={{ color: '#28a745', mr: 1.5, fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            {en_ja['Weight'] || 'Weight'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.875rem' }}>
                                            {weightKg} kg
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {Number.isFinite(safeBMI) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                                    <BMIIcon sx={{ color: '#6f42c1', mr: 1.5, fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            {en_ja['BMI'] || 'BMI'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.875rem' }}>
                                                {safeBMI}
                                            </Typography>
                                            {bmiCategory && (
                                                <Chip
                                                    label={bmiCategory.label}
                                                    size="small"
                                                    sx={{ backgroundColor: bmiCategory.color, color: 'white', fontSize: '0.7rem', height: '20px', fontWeight: 600 }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {checkinData?.sex && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                                    <PersonIcon sx={{ color: '#fd7e14', mr: 1.5, fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            {en_ja['Gender'] || 'Gender'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.875rem' }}>
                                            {checkinData.sex}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {checkinData?.symptoms && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 2, backgroundColor: '#fff3cd', borderRadius: '12px', border: '1px solid #ffeaa7' }}>
                                    <SymptomsIcon sx={{ color: '#856404', mr: 1.5, fontSize: 20, mt: 0.2 }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: '#856404', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', mb: 0.5, display: 'block' }}>
                                            {en_ja['Symptoms'] || 'Symptoms'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#856404', fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.5 }}>
                                            {checkinData.symptoms}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {activeTab === 1 && (
                    <Box sx={{ p: 3 }}>
                        {healthWarnings.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                {healthWarnings.map((warning, index) => (
                                    <Box key={index} sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 2,
                                        mb: index < healthWarnings.length - 1 ? 2 : 0,
                                        backgroundColor: warning.type === 'critical' ? '#f8d7da' : '#fff3cd',
                                        borderRadius: '12px',
                                        border: `1px solid ${warning.type === 'critical' ? '#f5c6cb' : '#ffeaa7'}`
                                    }}>
                                        <WarningIcon sx={{ color: warning.type === 'critical' ? '#721c24' : '#856404', fontSize: 20 }} />
                                        <Typography variant="body2" sx={{
                                            color: warning.type === 'critical' ? '#721c24' : '#856404',
                                            fontWeight: 500
                                        }}>
                                            {warning.message}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {userQuery.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '1rem', mb: 2 }}>
                                    {en_ja['Initial Health Assessment'] || 'Initial Health Assessment'}
                                </Typography>
                                <Box sx={{ display: 'grid', gap: 2, mb: 4 }}>
                                    {userQuery.map((question) => (
                                        <Box key={question.question_id} sx={{
                                            p: 2,
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '12px',
                                            border: '1px solid #e9ecef'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                                                {getQuestionIcon(question.question_text)}
                                                <Typography variant="body2" fontWeight="600" sx={{ color: '#1a1a1a', flex: 1 }}>
                                                    {question.question_text}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ ml: 3.5 }}>
                                                {question.answer_text && (
                                                    <Typography variant="body2" sx={{
                                                        backgroundColor: 'white',
                                                        p: 1.5,
                                                        borderRadius: '8px',
                                                        border: '1px solid #e9ecef',
                                                        fontWeight: 500
                                                    }}>
                                                        {question.answer_text}
                                                    </Typography>
                                                )}
                                                {question.types && (
                                                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                                        {question.types.map((type, index) => (
                                                            <Box key={index} sx={{
                                                                backgroundColor: 'white',
                                                                p: 1.5,
                                                                borderRadius: '8px',
                                                                border: '1px solid #e9ecef',
                                                                minWidth: '80px'
                                                            }}>
                                                                <Typography variant="caption" color="text.secondary" sx={{
                                                                    textTransform: 'uppercase',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.7rem',
                                                                    display: 'block'
                                                                }}>
                                                                    {type.type}
                                                                </Typography>
                                                                <Typography variant="body2" fontWeight="500">
                                                                    {type.answer_text}
                                                                    {type.type === 'Height' && ' cm'}
                                                                    {type.type === 'Weight' && ' kg'}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                                {question.options && (
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        {question.options.map((option, index) => (
                                                            <Chip
                                                                key={index}
                                                                label={option.option_text}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: '#dc3545',
                                                                    color: 'white',
                                                                    fontWeight: 500,
                                                                    '& .MuiChip-label': {
                                                                        fontSize: '0.75rem'
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}

                        {finalConsultationAnswers.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '1rem', mb: 2 }}>
                                    {en_ja['Final Consultation Assessment'] || 'Final Consultation Assessment'}
                                </Typography>
                                <Box sx={{ display: 'grid', gap: 2 }}>
                                    {finalConsultationAnswers.map((question) => (
                                        <Box key={question.question_id} sx={{
                                            p: 2,
                                            backgroundColor: '#e3f2fd',
                                            borderRadius: '12px',
                                            border: '1px solid #bbdefb'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                                                {getQuestionIcon(question.question_text)}
                                                <Typography variant="body2" fontWeight="600" sx={{ color: '#1a1a1a', flex: 1 }}>
                                                    {question.question_text}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ ml: 3.5 }}>
                                                {question.answer_text ? (
                                                    <Typography variant="body2" sx={{
                                                        backgroundColor: 'white',
                                                        p: 1.5,
                                                        borderRadius: '8px',
                                                        border: '1px solid #bbdefb',
                                                        fontWeight: 500
                                                    }}>
                                                        {question.answer_text}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2" sx={{
                                                        backgroundColor: '#fff3e0',
                                                        p: 1.5,
                                                        borderRadius: '8px',
                                                        border: '1px solid #ffcc02',
                                                        fontWeight: 500,
                                                        color: '#e65100',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        {en_ja['No response provided'] || 'No response provided'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}

                        {userQuery.length === 0 && finalConsultationAnswers.length === 0 && (
                            <Box sx={{ p: 2, borderRadius: '12px', border: '1px dashed #e9ecef', color: '#666', fontSize: '0.875rem', textAlign: 'center' }}>
                                {en_ja['No health assessment data available.'] || 'No health assessment data available.'}
                            </Box>
                        )}
                    </Box>
                )}

                {hasNotes && activeTab === 2 && (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '1rem', mb: 3 }}>
                            {en_ja['Consultation Notes'] || 'Consultation Notes'}
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            p: 3,
                            backgroundColor: '#f8f9fa',
                            borderRadius: '12px',
                            border: '1px solid #e9ecef'
                        }}>
                            <NotesIcon sx={{ color: '#dc3545', fontSize: 24, mt: 0.2 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{
                                    color: '#666',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    fontSize: '0.7rem',
                                    mb: 1,
                                    display: 'block'
                                }}>
                                    {en_ja["Doctor's Notes"] || "Doctor's Notes"}
                                </Typography>
                                <Typography variant="body1" sx={{
                                    color: '#1a1a1a',
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {appointmentData.notes}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PatientDetailsDialog;
